import { ReactNode, useLayoutEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import CircularProgress from 'components/shared/CircularProgress';
import Tooltip from 'components/shared/Tooltip';
import { colors } from 'styles/theme/colors';
import InfoIconOutline from 'svg/InfoIconOutline';

type Tone = 'green' | 'red' | 'default';

type StatTileProps = {
  label: string;
  primary: ReactNode;
  addendum?: ReactNode;
  tone?: Tone;
  size?: 'large' | 'medium';
  loading?: boolean;
  // When true, the addendum sits inline to the right of the primary value (baseline-aligned)
  // instead of stacked underneath.
  inlineAddendum?: boolean;
  // Optional explanatory hint shown via an info icon next to the label (e.g. "Based on rate").
  tooltipText?: string;
};

// Vertical stack: label, big primary value, optional small grey addendum (underneath, or inline
// to the right when `inlineAddendum`). Used for every metric panel in the slideout headers.
export function StatTile({
  label,
  primary,
  addendum,
  tone = 'default',
  size = 'large',
  loading = false,
  inlineAddendum = false,
  tooltipText,
}: StatTileProps) {
  // A colored em-dash reads as broken; fall back to default tone whenever the value is empty.
  const effectiveTone: Tone = loading || primary == null || primary === '\u2014' ? 'default' : tone;
  // The primary value ellipsis-truncates when it's wider than its column; only then does the
  // tooltip (which reveals the full value) earn its place. Measuring avoids a tooltip on every
  // card regardless of truncation. Re-measure on value change and on resize.
  const valueRef = useRef<HTMLSpanElement>(null);
  const [truncated, setTruncated] = useState(false);
  useLayoutEffect(() => {
    const el = valueRef.current;
    if (!el) {
      setTruncated(false);
      return;
    }
    const measure = () => setTruncated(el.scrollWidth > el.clientWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [primary, loading]);

  // Loading and populated render the exact same layout — only the value's text (and the addendum's
  // text) swap for a spinner / blank placeholder. Identical DOM means the tile is the same height
  // while loading as it is resolved, so it never jumps when the value lands or on a View By switch.
  const primaryValue = (
    <PrimaryValue ref={valueRef} $tone={effectiveTone} $size={size}>
      {loading ? (
        <LoadingSpinner>
          <CircularProgress color={colors.blue100} size={size === 'medium' ? 16 : 18} thickness={2} />
        </LoadingSpinner>
      ) : (
        primary
      )}
    </PrimaryValue>
  );
  const addendumNode = addendum != null && <Addendum aria-hidden={loading}>{loading ? '\u00a0' : addendum}</Addendum>;
  return (
    <Tooltip text={primary} disabled={loading || !truncated} position='top'>
      <StatBox>
        <StatLabel>
          {label}
          {!!tooltipText && (
            <Tooltip text={tooltipText} position='top'>
              <InfoIconOutline width={12} height={12} fill={colors.black50} style={{ marginLeft: 6, flexShrink: 0 }} />
            </Tooltip>
          )}
        </StatLabel>
        {inlineAddendum ? (
          <InlineRow>
            {primaryValue}
            {addendumNode}
          </InlineRow>
        ) : (
          <>
            {primaryValue}
            {addendumNode}
          </>
        )}
      </StatBox>
    </Tooltip>
  );
}

const StatBox = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.black10};
  border-radius: 4px;
  padding: 20px 22px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  // Allow children to shrink below their content width so ellipsis truncation actually kicks in
  // when the tile is in a grid column narrower than the value/addendum text.
  min-width: 0;
`;

export const TilesGrid = styled.div<{ $columns?: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $columns = 3 }) => $columns}, 1fr);
  gap: 12px;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
    gap: 8px;

    ${StatBox} {
      background-color: #f2f3f4;
      border: none;
    }

    ${({ $columns }) =>
      $columns === 4 &&
      css`
        grid-template-columns: 1fr 1fr;
        gap: 8px 4px;
      `}
  }
`;

const StatLabel = styled.span`
  display: flex;
  align-items: center;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.black75};
`;

const PrimaryValue = styled.span<{ $tone: Tone; $size: 'large' | 'medium' }>`
  font-size: ${({ $size }) => ($size === 'medium' ? '16px' : '20px')};
  font-weight: 500;
  line-height: 1.1;
  color: ${({ theme, $tone }) => {
    if ($tone === 'green') return theme.colors.green100;
    if ($tone === 'red') return theme.colors.red100;
    return theme.colors.black100;
  }};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// Occupies the same line box as PrimaryValue (same font-size + line-height) so the loading spinner
// reserves exactly the primary value's height — the tile doesn't resize when the value arrives.
// Sits inline inside PrimaryValue so the spinner rides the value's own line box — the tile keeps the
// populated value's exact height without any hardcoded pixel math.
const LoadingSpinner = styled.span`
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
`;

// Primary value + addendum on one baseline-aligned row (inlineAddendum layout).
const InlineRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
`;

const Addendum = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.black50};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
